// ---------- la fin d'une partie ----------
// La maquette « K ». On ne perd jamais, on gagne seulement moins : trois étoiles sans indice, deux
// avec un, une au-delà (src/store.ts, `helpStars`). Puis le mot mystère se retourne lettre à
// lettre, ses expressions, et la collection du chapitre — c'est elle, plus qu'un score, qui donne
// envie de voir la suite. En bas : le chemin, ou la partie suivante.

import { gridWord } from '../shared/normalize';
import type { ChapterSummary, Content, PuzzlePayload } from '../shared/types';
import { rain } from './fx';
import { t } from './i18n';
import { toUnlock } from './quest';
import { doneTotal, isDone, mysteryOf, progress } from './store';
import { inviteCard, shouldInvite } from './install';
import { shareResult } from './share';
import { $, esc, ICON, STAR, topBar } from './ui';

export interface EndContext {
  content: Content;
  /** le chapitre de la partie, ou null pour le mot du jour */
  chapterIndex: number | null;
  stars: number;
  /** le chapitre suivant vient-il de s'ouvrir avec cette partie ? */
  opened: ChapterSummary | null;
  streak: number;
  hasNext: boolean;
  /** Ce que le résultat partagé dit de la partie (« mot du jour du 2 octobre », « La cuisine, partie 3 »). */
  shareLabel: string;
  back(): void;
  next(): void;
}

let ctx: EndContext;
let shown: PuzzlePayload;

/** L'invitation à installer l'application, au moment où l'on a envie de continuer : à la
 *  troisième partie finie (assez pour savoir qu'on aime), puis toutes les cinq — jamais à chaque
 *  fin, ce serait une publicité de plus (src/install.ts). */
function invite(): string {
  const n = doneTotal();
  return shouldInvite() && (n === 3 || (n > 3 && n % 5 === 0)) ? inviteCard() : '';
}

/**
 * Les mots de la partie, chacun repliable : en face de chaque mot offert — et des indices achetés —,
 * l'expression qui le reliait à la réponse (shared/types.ts : `expressions` est aligné sur les six
 * mots). C'est le « ah, c'était ça ! » du jeu ; replié, parce que huit mots dépliés d'office
 * noieraient la collection en dessous.
 */
function wordsOf(p: PuzzlePayload): string {
  const st = progress(p.id);
  const items = p.lines
    .map((l, i) => {
      const e = l.enigma;
      const shown = [...e.clues, ...e.hints.slice(0, st.h[i] || 0)];
      const rows = shown
        .map((w, k) =>
          e.expressions[k] ? `<li><b>${esc(w)}</b><span>${esc(e.expressions[k])}</span></li>` : '',
        )
        .join('');
      return rows ? `<details><summary>${esc(e.answer)}</summary><ul>${rows}</ul></details>` : '';
    })
    .join('');
  return items ? `<div class="end-words"><small>${t('end.words')}</small>${items}</div>` : '';
}

/** Sous la collection : ce qui reste pour ouvrir la suite, ou le chapitre fini. Rien quand la
 *  suite vient de s'ouvrir — le bandeau jaune le dit déjà —, ni quand elle l'était déjà. */
function albumNote(c: EndContext, ch: ChapterSummary): string {
  if (c.opened) return '';
  const left = toUnlock(c.content, c.chapterIndex!);
  if (left) return `<p>${t('end.unlock', { n: left })}</p>`;
  return ch.puzzles.every((x) => isDone(x.id)) ? `<p>${t('end.complete')}</p>` : '';
}
let wired = false;

export function showEnd(p: PuzzlePayload, c: EndContext): void {
  ctx = c;
  shown = p;
  const ch = c.chapterIndex === null ? null : c.content.chapters[c.chapterIndex];
  const line = ch ? t('bar.chapter', { n: ch.position, title: ch.title }) : t('daily.bar');
  const starsLine = t(`end.stars${Math.max(1, Math.min(3, c.stars)) as 1 | 2 | 3}`);
  const tiles = [...gridWord(p.mystery)]
    .map((x, i) => `<span class="ftile" style="--d:${0.55 + i * 0.08}s">${x}</span>`)
    .join('');
  const album = ch
    ? `<div class="album">
        <div class="album-head"><b>${t('end.collection', { title: esc(ch.title) })}</b><span>${ch.puzzles.filter((x) => mysteryOf(x.id)).length} / ${ch.puzzles.length}</span></div>
        <div class="album-grid">${ch.puzzles
          .map((x) => {
            const w = mysteryOf(x.id);
            return `<span class="${x.id === p.id ? 'new' : w ? 'have' : ''}">${w ? esc(gridWord(w)) : '?'}</span>`;
          })
          .join('')}</div>
        ${albumNote(c, ch)}
      </div>`
    : `<p class="end-daily">${t('end.daily', { n: c.streak })}</p>`;
  $('end').innerHTML = `
    ${topBar(line, `<button class="icon-btn" type="button" data-act="back" aria-label="${t('bar.close')}">${ICON.close}</button>`)}
    <div class="end-body">
      <div class="end-stars" role="img" aria-label="${c.stars} / 3">${[1, 2, 3].map((k) => `<i class="${k <= c.stars ? 'on' : ''}" style="--d:${k * 0.18}s">${STAR}</i>`).join('')}</div>
      <p class="end-line">${starsLine}</p>
      <button class="btn btn-soft end-share" type="button" data-act="share">${ICON.share}${t('share.cta')}</button>
      <div class="end-word" aria-label="${esc(p.mystery)}">${tiles}</div>
      ${p.expressions.length ? `<div class="end-expr"><small>${t('end.expr')}</small>${p.expressions.map((x) => `<span>${esc(x)}</span>`).join('')}</div>` : ''}
      ${wordsOf(p)}
      ${c.opened ? `<div class="end-opened">${t('end.opened', { title: esc(c.opened.title) })}</div>` : ''}
      ${album}
      ${invite()}
    </div>
    <div class="end-actions">
      <button class="btn btn-soft" type="button" data-act="back">${ICON.path}${t('end.path')}</button>
      ${c.hasNext ? `<button class="btn btn-yellow" type="button" data-act="next">${t('end.next')}${ICON.next}</button>` : ''}
    </div>`;
  if (!wired) {
    wired = true;
    $('end').addEventListener('click', (e) => {
      const act = (e.target as HTMLElement).closest<HTMLElement>('[data-act]')?.dataset.act;
      if (act === 'back') ctx.back();
      if (act === 'next') ctx.next();
      if (act === 'share') void shareResult(shown, ctx.stars, ctx.shareLabel);
    });
  }
  rain();
}
